export function aiolosRecordEvent(
  id: string,
  eventData: Record<string, any>,
  async = true,
) {
  try {
    if (import.meta.server) {
      return;
    }
    console.log("eventData", eventData);
    // 分析-简单事件/埋点
    (window as any).bbAnalysis.event(
      {
        eventId: id,
        eventData,
      },
      async,
    );
  } catch (error) {
    console.log("aiolosRecordEvent失败：", error);
  }
}
// utils/download.ts

export interface DownloadResult {
  /** 是否成功 */
  success: boolean;
  /** 失败时的错误信息 */
  error?: string;
  /** 文件名 */
  filename: string;
  /** 文件大小（MB，保留两位小数） */
  sizeMB: number;
  /** 文件 MIME 类型 */
  type: string;
  /** 本地可访问地址（成功时才有，调用方如需释放可自行 revoke） */
  localUrl?: string;
  /** 下载耗时（毫秒） */
  duration: number;
}

export interface DownloadOptions {
  /** 自定义文件名，不传则从响应头或 URL 推断 */
  filename?: string;
  /** 下载进度回调，参数为已下载字节数和总字节数 */
  onProgress?: (loaded: number, total: number) => void;
  /** 是否自动触发浏览器下载，默认 true */
  autoDownload?: boolean;
  /** 是否在下载后自动释放 Object URL，默认 true */
  autoRevoke?: boolean;
}

/**
 * 下载文件并在方法内触发浏览器下载
 * @param url 文件地址
 * @param options 可选配置
 * @returns 下载结果，包含成功/失败状态、文件信息
 */
export async function downloadFileWithInfo(
  url: string,
  options: DownloadOptions = {},
): Promise<DownloadResult> {
  const {
    filename: customFilename,
    onProgress,
    autoDownload = true,
    autoRevoke = true,
  } = options;

  const startTime = Date.now();

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`下载失败：${response.status} ${response.statusText}`);
    }

    // 文件大小：优先用响应头
    const contentLength = response.headers.get("Content-Length");
    const total = contentLength ? Number(contentLength) : 0;

    // 文件名：自定义 > Content-Disposition > URL
    let filename = customFilename || "";
    if (!filename) {
      const disposition = response.headers.get("Content-Disposition");
      if (disposition) {
        const match = disposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (match && match[1]) {
          filename = decodeURIComponent(match[1].replace(/['"]/g, ""));
        }
      }
    }
    if (!filename) {
      filename = url.split("/").pop()?.split("?")[0] || "download";
    }

    // 带进度地读取
    let blob: Blob;
    if (onProgress && response.body && total > 0) {
      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let loaded = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        loaded += value.byteLength;
        onProgress(loaded, total);
      }

      blob = new Blob(chunks as BlobPart[], {
        type:
          response.headers.get("Content-Type") || "application/octet-stream",
      });
    } else {
      blob = await response.blob();
    }

    const duration = Date.now() - startTime;
    const localUrl = URL.createObjectURL(blob);

    // 方法内直接触发 a 标签下载
    if (autoDownload) {
      const a = document.createElement("a");
      a.href = localUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      if (autoRevoke) {
        // 延迟释放，避免部分浏览器下载还没启动就 revoke
        setTimeout(() => URL.revokeObjectURL(localUrl), 1000);
      }
    }

    return {
      success: true,
      filename,
      sizeMB: Number((blob.size / 1024 / 1024).toFixed(2)),
      type: blob.type,
      localUrl,
      duration: Math.round(duration / 1000),
    };
  } catch (error) {
    const duration = Date.now() - startTime;
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
      filename: customFilename || "",
      sizeMB: 0,
      type: "",
      duration: Math.round(duration / 1000),
    };
  }
}
